import { useAutocompleteInput } from '@bridebook/web/app-basic/components/search/location-autocomplete/use-autocomplete-input';
import { getIsMobile } from '@bridebook/web/app-basic/lib/app/selectors';
import { useSelector } from '@bridebook/web/app-basic/lib/utils';
import {
  ChangeEvent,
  ChangeEventHandler,
  CSSProperties,
  FunctionComponent,
  ReactElement,
  memo,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react';
import { cn } from '@bridebook/aura';
import { IAddress, IWedding } from '@bridebook/models/source/models/Weddings.types';
import { CountryCodes } from '@bridebook/toolbox/src/gazetteer';
import {
  AutocompletePrediction,
  CustomAutocompletePrediction,
} from '@bridebook/web/app-shared/lib/search/types';
import { isCustomAutocompletePrediction } from '@bridebook/web/app-shared/lib/search/utils/custom-autocomplete-predictions';
import { googlePlacesService } from './services';
import { parseGooglePlace } from '@bridebook/web/app-shared/lib/search/utils/parse-google-place';
import { extractLocationName } from '@bridebook/web/app-shared/lib/search/utils/utils';
import { withGoogleMapsApi } from '@bridebook/web/app-shared/lib/search/utils/with-google-maps-api';
import { AutocompleteTextInput } from './autocomplete-text-input';
import { GooglePredictionsContext, IGooglePredictionsContext } from './context';
import { LocationPinIcon } from './icons';
import LocationModalAutocomplete from './location-modal-autocomplete';
import { LocationResults } from './location-results';
import { useSetAutocompletePredictions } from './use-set-autocomplete-predictions';

/** Location pin shown before the input unless the caller supplies its own icon. */
const defaultBeforeIcon = (
  <LocationPinIcon width={18} height={23} className="text-space-75 mr-3 shrink-0" />
);

/** Corner radius that flattens toward the open predictions dropdown. */
const inputRadiusClass = (hasDropdown: boolean, position: 'top' | 'bottom') => {
  if (!hasDropdown) return 'rounded-[35px]';
  return position === 'bottom' ? 'rounded-t-2xl rounded-b-none' : 'rounded-t-none rounded-b-[35px]';
};

export interface LoggedOutAutocompleteInputProps<T> {
  label?: string;
  placeholder: string;
  initialValue?: string;
  country?: CountryCodes;
  onChange?(data: T | null): void;
  onPlaceChanged?(place: google.maps.places.PlaceResult | CustomAutocompletePrediction): any;
  name?: string;
  onValueChange?(value: string, e: ChangeEvent<HTMLInputElement>): void;
  forceEnableGlobalAutocomplete?: boolean;
  style?: CSSProperties;
  disabled?: boolean;
  afterInput?: ReactElement;
  onFocusProp?: () => void;
  onBlurProp?: () => void;
  beforeIcon?: ReactElement;
}

const AutocompleteInputLocationComponent: FunctionComponent<
  LoggedOutAutocompleteInputProps<IAddress>
> = (props) => {
  const {
    placeholder,
    label,
    country,
    onPlaceChanged,
    name,
    onValueChange,
    style,
    disabled,
    afterInput,
    onFocusProp,
    onBlurProp,
    beforeIcon,
  } = props;
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { predictions } = useContext(GooglePredictionsContext);
  const hasPredictions = predictions.length > 0;
  const [resultsPosition, setResultsPosition] = useState<'top' | 'bottom'>('bottom');
  const [showModal, setShowModal] = useState(false);
  const isMobile = useSelector(getIsMobile);

  const { value, showSearchResults, onChange, onClick, onBlur, onEscape, onFocus } =
    useAutocompleteInput<IAddress | AutocompletePrediction>(props);

  const handleOnFocus = useCallback(() => {
    if (isMobile) setShowModal(true);
    onFocus();
    onFocusProp?.();
  }, [isMobile, onFocus, onFocusProp]);

  const handleOnBlur = useCallback(() => {
    onBlur();
    onBlurProp?.();
  }, [onBlur, onBlurProp]);

  const handleChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    onChange(e);
    onValueChange?.(value, e);
  };

  const onClickCb = useCallback(
    (
      prediction: AutocompletePrediction,
      googlePredictions: IGooglePredictionsContext['googlePredictions'],
    ) => {
      const updateValue = (address: IAddress) => {
        onClick(extractLocationName(address), address);
      };
      (async () => {
        if (!isCustomAutocompletePrediction(prediction)) {
          // for raw google predictions we first need to fetch place details
          const rawPrediction = googlePredictions[prediction.place_id];
          const place = await googlePlacesService.getPlaceDetails([rawPrediction]);
          onPlaceChanged?.(place);
          const location: IWedding['location'] = parseGooglePlace(place);

          // update local state with parsed google place
          updateValue(location);
        } else {
          onPlaceChanged?.(prediction);
          // update local state with custom prediction address
          updateValue(prediction.address);
        }
      })();
      if (isMobile) setShowModal(false);
    },
    [isMobile, onClick, onPlaceChanged],
  );

  const closeModal = useCallback(() => {
    setShowModal(false);
  }, []);

  // set autocomplete context using users input
  useSetAutocompletePredictions(value, { country, debounceTime: 500 });

  return (
    <>
      {showModal ? (
        <LocationModalAutocomplete
          label={label}
          inputPlaceholder={placeholder}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          onClickCb={onClickCb}
          onEscape={onEscape}
          setResultsPosition={setResultsPosition}
          closeModal={closeModal}
          showModal={showModal}
          showSearchResults={showSearchResults}
        />
      ) : (
        <div className="bsauto-wrap" ref={wrapperRef}>
          <AutocompleteTextInput
            disabled={disabled}
            name={name}
            dataTest="autocomplete-input-location"
            onChange={handleChange}
            onBlur={handleOnBlur}
            onFocus={handleOnFocus}
            placeholder={placeholder}
            beforeInput={beforeIcon ?? defaultBeforeIcon}
            afterInput={afterInput}
            value={value}
            className={cn(
              'h-11',
              inputRadiusClass(showSearchResults && hasPredictions, resultsPosition),
            )}
            style={style}
          />
          {showSearchResults && (
            <LocationResults
              input={value}
              onClick={onClickCb}
              parentRef={wrapperRef.current}
              onEscape={onEscape}
              onPositionChange={setResultsPosition}
            />
          )}
        </div>
      )}
    </>
  );
};

const AutocompleteInputLocationFallback: FunctionComponent<
  LoggedOutAutocompleteInputProps<IAddress>
> = ({ placeholder, style, afterInput }) => (
  <div className="bsauto-wrap">
    <AutocompleteTextInput
      disabled
      placeholder={placeholder}
      beforeInput={defaultBeforeIcon}
      afterInput={afterInput}
      className="h-11 rounded-[35px]"
      style={style}
    />
  </div>
);

export const AutocompleteInputLocation = memo(
  withGoogleMapsApi(AutocompleteInputLocationComponent, AutocompleteInputLocationFallback),
);
